import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  aICreatorSuggestSectionTitleAction,
  aICreatorSuggestPageTitleAction,
  aICreatorUpdateOutlineAction,
  aICreatorSuggestPageOutlineAction,
} from 'src/reducers/ai-creator/action'
import {
  addSection as addSectionUtil,
  removeSection as removeSectionUtil,
  duplicateSectionData,
  updateSectionTitle as updateSectionTitleUtil,
  addModule as addModuleUtil,
  removeModule as removeModuleUtil,
  updateModuleTitle as updateModuleTitleUtil,
  addGoal as addGoalUtil,
  removeGoal as removeGoalUtil,
  updateGoal as updateGoalUtil,
} from '../utils/sectionUtils'

export const useSectionEdit = (data, dispatch, setCreditCost) => {
  const localDispatch = useDispatch()

  // Get the course topic from Redux store
  const courseTopic = useSelector(state => state.aICreator.topic)
  const courseLanguageCode = useSelector(state => state.aICreator.languageCode)

  // State management
  const [expandedModules, setExpandedModules] = useState({})
  const [sectionMenuAnchor, setSectionMenuAnchor] = useState(null)
  const [activeSectionIndex, setActiveSectionIndex] = useState(null)
  const [popupStates, setPopupStates] = useState({})
  const [anchorElements, setAnchorElements] = useState({})
  const [focusedGoalIndex, setFocusedGoalIndex] = useState(null)
  const [selectingSuggestion, setSelectingSuggestion] = useState(false)
  const [popupLoadingStates, setPopupLoadingStates] = useState({})
  const [fieldSuggestions, setFieldSuggestions] = useState({})
  const [sectionTitleSuggestions, setSectionTitleSuggestions] = useState([])
  const [pageTitleSuggestions, setPageTitleSuggestions] = useState([])
  const [showAllPageDetails, setShowAllPageDetails] = useState(false)
  const [isCourseHasDocument] = useState(true)
  const attachments = useSelector(state => state.aICreator.attachments)

  // Refs
  const sectionTitleRefs = useRef([])
  const moduleTitleRefs = useRef([])
  const goalRefs = useRef([])
  const modalRef = useRef(null)
  const presentedSuggestionsRef = useRef({}) // Ref to track previously generated suggestions for regeneration

  // Clear presented suggestions when course topic changes
  useEffect(() => {
    presentedSuggestionsRef.current = {}
  }, [courseTopic])

  // Initialize modules with only first section's first page expanded when component mounts
  useEffect(() => {
    if (
      data?.response?.outline?.sections &&
      Object.keys(expandedModules).length === 0
    ) {
      const initialExpandedModules = {}
      data.response.outline.sections.forEach((section, sectionIndex) => {
        section.modules.forEach((module, moduleIndex) => {
          const key = `${sectionIndex}-${moduleIndex}`

          // Only expand the first section's first page by default
          if (sectionIndex === 0 && moduleIndex === 0) {
            initialExpandedModules[key] = true
          } else if (
            // Always expand survey and quiz modules
            module.content_type === 'survey' ||
            module.content_type === 'quiz'
          ) {
            initialExpandedModules[key] = true
          } else {
            // Keep all other modules collapsed by default
            initialExpandedModules[key] = false
          }
        })
      })
      setExpandedModules(initialExpandedModules)
    }
  }, [data?.response?.outline?.sections, expandedModules])

  // Auto-expand new modules when they're added
  useEffect(() => {
    // Only run this effect when sections are added/removed, not when showAllPageDetails changes
    // Initialize new modules with appropriate default expanded state
    const newExpandedModules = { ...expandedModules }
    let needsUpdate = false

    data.response.outline.sections.forEach((section, sectionIndex) => {
      section.modules.forEach((module, moduleIndex) => {
        const key = `${sectionIndex}-${moduleIndex}`
        if (!(key in newExpandedModules)) {
          // Only expand the first section's first page by default
          if (sectionIndex === 0 && moduleIndex === 0) {
            newExpandedModules[key] = true
          } else if (
            // Always expand survey and quiz modules
            module.content_type === 'survey' ||
            module.content_type === 'quiz'
          ) {
            newExpandedModules[key] = true
          } else {
            // Keep all other modules collapsed by default
            newExpandedModules[key] = false
          }
          needsUpdate = true
        }
      })
    })

    // Ensure survey and quiz modules are always expanded
    data.response.outline.sections.forEach((section, sectionIndex) => {
      section.modules.forEach((module, moduleIndex) => {
        const key = `${sectionIndex}-${moduleIndex}`
        if (
          module.content_type === 'survey' ||
          module.content_type === 'quiz'
        ) {
          if (!newExpandedModules[key]) {
            newExpandedModules[key] = true
            needsUpdate = true
          }
        }
      })
    })

    if (needsUpdate) {
      setExpandedModules(newExpandedModules)
    }
  }, [data?.response?.outline?.sections?.length])

  useEffect(() => {
    const sections = data?.response?.outline?.sections
    if (!sections) return

    let needsUpdate = false
    const newSections = sections.map(section => ({
      ...section,
      modules: section.modules.map(module => {
        const newModule = { ...module }

        // Only add goals for modules that are not survey or quiz
        const shouldHaveGoals =
          module.content_type !== 'survey' && module.content_type !== 'quiz'

        if (shouldHaveGoals) {
          if (!newModule.outline) {
            newModule.outline = []
          }
          if (
            newModule.outline.length === 0 ||
            newModule.outline[newModule.outline.length - 1] !== ''
          ) {
            newModule.outline.push('')
            needsUpdate = true
          }
        } else {
          // For survey and quiz modules, ensure they have no goals
          newModule.outline = []
        }

        return newModule
      }),
    }))

    if (needsUpdate) {
      handleOutlineUpdate(newSections)
    }
  }, [data?.response?.outline?.sections])

  // Centralized outline update handler
  const handleOutlineUpdate = newSections => {
    dispatch(
      aICreatorUpdateOutlineAction(
        { outline: { ...data.response.outline, sections: newSections } },
        data.response.outlineId,
        response => {
          setCreditCost(response.credit_cost)
        },
      ),
    )
  }

  // Section management
  const addSection = () => {
    const newSections = addSectionUtil(data.response.outline.sections)
    handleOutlineUpdate(newSections)

    // Auto-focus the new section title and show suggestions
    const newSectionIndex = newSections.length - 1
    setTimeout(() => {
      // Focus the new section title field
      if (sectionTitleRefs.current[newSectionIndex]) {
        sectionTitleRefs.current[newSectionIndex].focus()
        // Manually trigger title suggestions for the new section since focus() doesn't trigger onFocus event
        handleSectionTitleSuggestions(newSectionIndex)
      }
    }, 100)
  }

  const removeSection = sectionIndex => {
    const newSections = removeSectionUtil(
      data.response.outline.sections,
      sectionIndex,
    )
    handleOutlineUpdate(newSections)
  }
  const deletePage = (sectionIndex, moduleIndex) => {
    const newSections = removeModuleUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
    )
    handleOutlineUpdate(newSections)
  }

  const duplicateSection = sectionIndex => {
    const newSections = [...data.response.outline.sections]
    const duplicated = duplicateSectionData(newSections[sectionIndex])
    newSections.splice(sectionIndex + 1, 0, duplicated)
    handleOutlineUpdate(newSections)
  }

  const handleSectionTitleChange = (value, sectionIndex) => {
    const newSections = updateSectionTitleUtil(
      data.response.outline.sections,
      sectionIndex,
      value,
    )
    handleOutlineUpdate(newSections)
  }

  // Module management
  const addModule = sectionIndex => {
    const newSections = addModuleUtil(
      data.response.outline.sections,
      sectionIndex,
    )
    handleOutlineUpdate(newSections)
  }

  const removeModule = (sectionIndex, moduleIndex) => {
    const newSections = removeModuleUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
    )
    handleOutlineUpdate(newSections)
  }

  const handleModuleTitleChange = (value, sectionIndex, moduleIndex) => {
    const newSections = updateModuleTitleUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      value,
    )
    handleOutlineUpdate(newSections)
  }

  // Goal management
  const addGoal = (sectionIndex, moduleIndex, goalText = '') => {
    // Don't add goals to survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const newSections = addGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalText,
    )
    handleOutlineUpdate(newSections)
  }

  // This is now redundant, as addGoal handles both cases
  const addGoalWithText = (sectionIndex, moduleIndex, goalText) => {
    // Don't add goals to survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    let newSections = addGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalText,
    )
    newSections = addGoalUtil(newSections, sectionIndex, moduleIndex, '')
    handleOutlineUpdate(newSections)
  }

  const removeGoal = (sectionIndex, moduleIndex, goalIndex) => {
    // Don't remove goals from survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const newSections = removeGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalIndex,
    )
    handleOutlineUpdate(newSections)
  }

  // Special function for removing the last goal from NewGoalInput
  // This doesn't trigger focus management logic to avoid text clearing issues
  const removeLastGoal = (sectionIndex, moduleIndex, goalIndex) => {
    // Don't remove goals from survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const newSections = removeGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalIndex,
    )
    handleOutlineUpdate(newSections)
  }

  const handleGoalChange = (value, sectionIndex, moduleIndex, goalIndex) => {
    // Don't modify goals for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    let newSections = updateGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalIndex,
      value,
    )

    const goals = newSections[sectionIndex].modules[moduleIndex].outline
    // If the user just filled in the last (empty) goal, add a new empty one
    if (goalIndex === goals.length - 1 && value.trim() !== '') {
      newSections = addGoalUtil(newSections, sectionIndex, moduleIndex, '')
    }
    handleOutlineUpdate(newSections)
  }

  // Module expansion management
  const toggleModule = moduleKey => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }))
  }

  const handlePageDetailsToggle = () => {
    const newShowAllPageDetails = !showAllPageDetails
    setShowAllPageDetails(newShowAllPageDetails)

    if (newShowAllPageDetails) {
      // Expand all modules
      const allExpanded = {}
      data.response.outline.sections.forEach((section, sectionIndex) => {
        section.modules.forEach((module, moduleIndex) => {
          const key = `${sectionIndex}-${moduleIndex}`
          allExpanded[key] = true
        })
      })
      setExpandedModules(allExpanded)
    } else {
      // Collapse all modules except survey and quiz (they should always be expanded)
      const collapsedModules = {}
      data.response.outline.sections.forEach((section, sectionIndex) => {
        section.modules.forEach((module, moduleIndex) => {
          const key = `${sectionIndex}-${moduleIndex}`
          if (
            module.content_type === 'survey' ||
            module.content_type === 'quiz'
          ) {
            // Survey and quiz modules should always remain expanded
            collapsedModules[key] = true
          } else {
            // All other modules should be collapsed (set to false)
            collapsedModules[key] = false
          }
        })
      })
      setExpandedModules(collapsedModules)
    }
  }

  // Section menu management
  const handleSectionMenuOpen = (event, sectionIndex) => {
    setSectionMenuAnchor(event.currentTarget)
    setActiveSectionIndex(sectionIndex)
  }

  const handleSectionMenuClose = () => {
    setSectionMenuAnchor(null)
    setActiveSectionIndex(null)
  }

  const handleSectionAction = action => {
    if (activeSectionIndex !== null) {
      if (action === 'duplicate') {
        duplicateSection(activeSectionIndex)
      } else if (action === 'delete') {
        removeSection(activeSectionIndex)
      }
    }
    handleSectionMenuClose()
  }

  // Goal suggestions management
  const handleRegenerateSuggestions = (
    sectionIndex,
    moduleIndex,
    goalIndex,
  ) => {
    // Don't generate suggestions for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const courseAbout = courseTopic || 'AI Course Creation' // Use actual course topic from Redux
    const languageCode = courseLanguageCode || 'en' // Use actual course topic from Redux

    // Get current section and module data
    const currentSection = data.response.outline.sections[sectionIndex]
    const currentModule = currentSection?.modules[moduleIndex]

    // Only collect goals from the current page (module) instead of all pages
    const currentGoals = []
    if (
      currentModule &&
      currentModule.outline &&
      currentModule.outline !== null
    ) {
      currentGoals.push(
        ...currentModule.outline.filter(goal => goal && goal.trim() !== ''),
      )
    }

    const fieldKey = `${sectionIndex}-${moduleIndex}-${goalIndex}`

    // Get previously generated suggestions for this field
    const previousSuggestions = presentedSuggestionsRef.current[fieldKey] || []

    const requestData = {
      topic: courseAbout,
      languageCode,
      pageTitle: currentModule?.module_title || '',
      sectionTitle: currentSection?.section_title || '',
      currentOutline: currentGoals,
      attachmentIds: attachments?.map(att => att.attachment_id) || [],
      presentGoals: previousSuggestions, // Include previous suggestions to avoid duplicates
    }

    // Set loading state to true before making the API call
    setPopupLoadingStates(prev => ({
      ...prev,
      [fieldKey]: true,
    }))

    localDispatch(
      aICreatorSuggestPageOutlineAction(requestData, response => {
        let suggestions = []
        if (response && response.suggestedOutline) {
          suggestions = response.suggestedOutline
        }

        // Update ref with new suggestions
        presentedSuggestionsRef.current = {
          ...presentedSuggestionsRef.current,
          [fieldKey]: suggestions,
        }

        setFieldSuggestions(prev => ({
          ...prev,
          [fieldKey]: suggestions,
        }))

        setPopupLoadingStates(prev => ({
          ...prev,
          [fieldKey]: false,
        }))
      }),
    )
  }

  const handleGoalFocus = (sectionIndex, moduleIndex, goalIndex) => {
    // Don't handle goal focus for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const fieldKey = `${sectionIndex}-${moduleIndex}-${goalIndex}`
    setFocusedGoalIndex(fieldKey)

    // Always show suggestions when a goal is focused, regardless of whether it's empty
    setPopupStates(prev => ({
      ...prev,
      [fieldKey]: true,
    }))
    setAnchorElements(prev => ({
      ...prev,
      [fieldKey]: goalRefs.current[fieldKey],
    }))

    // If we already have suggestions for this field, don't fetch again
    if (fieldSuggestions[fieldKey]) {
      return
    }

    // Set loading state and fetch suggestions
    setPopupLoadingStates(prev => ({
      ...prev,
      [fieldKey]: true,
    }))

    handleRegenerateSuggestions(sectionIndex, moduleIndex, goalIndex)
  }

  // Special handler for new goal input focus
  const handleNewGoalFocus = (sectionIndex, moduleIndex, event) => {
    // Don't handle new goal focus for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    const fieldKey = `${sectionIndex}-${moduleIndex}-new`

    // Set the anchor element for this field
    setAnchorElements(prev => ({
      ...prev,
      [fieldKey]: event.target,
    }))

    // Only show suggestions if the field is empty or has minimal content
    const fieldContent = event.target.textContent || ''
    if (!fieldContent.trim() || fieldContent.trim().length < 2) {
      setPopupStates(prev => ({
        ...prev,
        [fieldKey]: true,
      }))

      // Set loading state to true before fetching suggestions
      setPopupLoadingStates(prev => ({
        ...prev,
        [fieldKey]: true,
      }))

      // Trigger suggestions for this field using the new page outline service
      handleRegenerateSuggestions(sectionIndex, moduleIndex, 'new')
    }
  }

  const handlePopupClose = fieldKey => {
    setPopupStates(prev => ({
      ...prev,
      [fieldKey]: false,
    }))
    setFocusedGoalIndex(null)
    setPopupLoadingStates(prev => ({
      ...prev,
      [fieldKey]: false,
    }))
  }

  const handleSuggestionClick = (
    sectionIndex,
    moduleIndex,
    goalIndex,
    suggestion,
  ) => {
    // Don't handle suggestion clicks for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    let newSections = updateGoalUtil(
      data.response.outline.sections,
      sectionIndex,
      moduleIndex,
      goalIndex,
      suggestion,
    )

    const goals = newSections[sectionIndex].modules[moduleIndex].outline
    if (goalIndex === goals.length - 1 && suggestion.trim() !== '') {
      newSections = addGoalUtil(newSections, sectionIndex, moduleIndex, '')
    }

    handleOutlineUpdate(newSections)

    const fieldKey = `${sectionIndex}-${moduleIndex}-${goalIndex}`

    setSelectingSuggestion(true)

    // Also update the DOM element directly
    const goalRef = goalRefs.current[fieldKey]
    if (goalRef) {
      goalRef.textContent = suggestion
    }

    // Don't clear the presentedSuggestions - keep them for regeneration
    // Only clear the fieldSuggestions for display purposes
    setFieldSuggestions(prev => ({
      ...prev,
      [fieldKey]: null,
    }))

    setTimeout(() => {
      handlePopupClose(fieldKey)
      setSelectingSuggestion(false)
    }, 150)
  }

  // Handle backspace key for goal deletion
  const handleGoalKeyDown = (event, sectionIndex, moduleIndex, goalIndex) => {
    // Don't handle goal keydown for survey or quiz modules
    const module =
      data.response.outline.sections[sectionIndex]?.modules[moduleIndex]
    if (
      module &&
      (module.content_type === 'survey' || module.content_type === 'quiz')
    ) {
      return
    }

    // If backspace is pressed and the goal is empty, delete the goal
    const goals =
      data.response.outline.sections[sectionIndex].modules[moduleIndex].outline
    const currentGoal = goals[goalIndex]
    if (
      event.key === 'Backspace' &&
      goals.length > 1 &&
      (currentGoal.trim() === '' || event.target.textContent.trim() === '')
    ) {
      removeGoal(sectionIndex, moduleIndex, goalIndex)

      // Focus on the previous goal if available, or the next goal
      setTimeout(() => {
        const prevGoalIndex = goalIndex - 1
        const nextGoalIndex = goalIndex

        if (prevGoalIndex >= 0) {
          const prevGoalKey = `${sectionIndex}-${moduleIndex}-${prevGoalIndex}`
          const prevGoalRef = goalRefs.current[prevGoalKey]
          if (prevGoalRef) {
            // Ensure the goal text is preserved before focusing
            const goalText =
              data.response.outline.sections[sectionIndex].modules[moduleIndex]
                .outline[prevGoalIndex]

            if (goalText && prevGoalRef.textContent !== goalText) {
              prevGoalRef.textContent = goalText
            }

            prevGoalRef.focus()
            // Place cursor at the end
            const range = document.createRange()
            const selection = window.getSelection()
            range.selectNodeContents(prevGoalRef)
            range.collapse(false)
            selection.removeAllRanges()
            selection.addRange(range)
          }
        } else if (
          nextGoalIndex <
          data.response.outline.sections[sectionIndex].modules[moduleIndex]
            .outline.length
        ) {
          const nextGoalKey = `${sectionIndex}-${moduleIndex}-${nextGoalIndex}`
          const nextGoalRef = goalRefs.current[nextGoalKey]
          if (nextGoalRef) {
            // Ensure the goal text is preserved before focusing
            const goalText =
              data.response.outline.sections[sectionIndex].modules[moduleIndex]
                .outline[nextGoalIndex]

            if (goalText && nextGoalRef.textContent !== goalText) {
              nextGoalRef.textContent = goalText
            }

            nextGoalRef.focus()
            // Place cursor at the end
            const range = document.createRange()
            const selection = window.getSelection()
            range.selectNodeContents(nextGoalRef)
            range.collapse(false)
            selection.removeAllRanges()
            selection.addRange(range)
          }
        }
      }, 0)
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      addGoal(sectionIndex, moduleIndex)
    }
  }

  // Section title suggestions
  const handleSectionTitleSuggestions = useCallback(
    sectionIndex => {
      const requestData = {
        outlineId: data.response.outlineId || '',
      }

      localDispatch(
        aICreatorSuggestSectionTitleAction(requestData, response => {
          let suggestions = []
          if (response && response.suggestedSectionTitles) {
            suggestions = response.suggestedSectionTitles
          }

          setSectionTitleSuggestions(suggestions)
        }),
      )
    },
    [data?.response?.outlineId, localDispatch],
  )

  // Page title suggestions
  const handlePageTitleSuggestions = useCallback(
    (sectionIndex, moduleIndex) => {
      const currentGoals = []
      data.response.outline.sections.forEach(section => {
        section.modules.forEach(module => {
          // Only collect goals from modules that are not survey or quiz
          if (
            module.content_type !== 'survey' &&
            module.content_type !== 'quiz' &&
            module.outline &&
            module.outline !== null
          ) {
            currentGoals.push(
              ...module.outline.filter(goal => goal && goal.trim() !== ''),
            )
          }
        })
      })

      const currentPageTitles = []
      data.response.outline.sections.forEach(section => {
        section.modules.forEach(module => {
          if (module.module_title && module.module_title.trim() !== '') {
            currentPageTitles.push(module.module_title)
          }
        })
      })

      const requestData = {
        outlineId: data?.response?.outlineId || '',
        type: 'info', // Default to info type for section edit
        sectionTitle:
          data?.response?.outline?.sections?.[sectionIndex]?.section_title ||
          '',
      }
      localDispatch(
        aICreatorSuggestPageTitleAction(requestData, response => {
          let suggestions = []
          if (response && response.suggestedPageTitles) {
            suggestions = response.suggestedPageTitles
          }

          setPageTitleSuggestions(suggestions)
        }),
      )
    },
    [
      data?.response?.outline?.sections,
      data?.response?.outlineId,
      localDispatch,
    ],
  )

  return {
    // State
    expandedModules,
    sectionMenuAnchor,
    activeSectionIndex,
    popupStates,
    anchorElements,
    focusedGoalIndex,
    selectingSuggestion,
    popupLoadingStates,
    fieldSuggestions,
    sectionTitleSuggestions,
    pageTitleSuggestions,
    showAllPageDetails,
    isCourseHasDocument,

    // Refs
    sectionTitleRefs,
    moduleTitleRefs,
    goalRefs,
    modalRef,

    // Actions
    addSection,
    removeSection,
    duplicateSection,
    handleSectionTitleChange,
    addModule,
    removeModule,
    handleModuleTitleChange,
    addGoal,
    addGoalWithText,
    removeGoal,
    removeLastGoal,
    handleGoalChange,
    toggleModule,
    handlePageDetailsToggle,
    handleSectionMenuOpen,
    handleSectionMenuClose,
    handleSectionAction,
    handleRegenerateSuggestions,
    handleGoalFocus,
    handleNewGoalFocus,
    handlePopupClose,
    handleSuggestionClick,
    handleGoalKeyDown,
    handleSectionTitleSuggestions,
    handlePageTitleSuggestions,
    deletePage,
  }
}
